import { Button, Header, Modal, Text } from '@gasbuddy/react-components';
import { Footer, NavBar } from '@gasbuddy/react-consumer-components';
import loadable from '@loadable/component';
import classnames from 'classnames/bind';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { version } from '../../../../package.json';
import useGoogleOneTap from '../../../lib/hooks/useGoogleOneTap';
import { analyticsScreen, canonicalizeString, localyticsEvent, scrollToTop, truncateForDisplay } from '../../../lib/utils';
import canShowDoNotSell from '../../../lib/utils/canShowDoNotSell';
import ReactGTM from '../../../lib/utils/gtm';
import { ANALYTICS_EVENTS, BANNER_ADS, COUNTRIES_WITH_PAY, COUNTRY_CODES } from '../../constants';
import { DESCRIPTION, THUMBNAIL, TITLE } from '../../constants/helmet';
import { AdSizeMappingPropType, AdSizesPropType } from '../../prop-types/ad';
import { LoginStatus } from '../../reducers/auth';
import { AdBanner } from '../AdBanner';
import AlertBanner from '../AlertBanner';
import ErrorBoundary from '../ErrorBoundary';
import MainSchema from '../MainSchema';
import styles from './Page.module.css';

const cx = classnames.bind(styles);

export const pageView = (name, meta) => {
  analyticsScreen(name);
  scrollToTop();
  localyticsEvent(`${name}_Opened`, meta);
  localyticsEvent('Screen Viewed', {
    ...meta,
    'Screen Name': name,
  });
};

const SocialSignupLazyComponent = loadable(() => import('./SocialSignup'));

export default function Page({
  activeNavKey,
  adParams,
  authLoginStatus,
  bannerAds,
  children,
  className,
  compact,
  country,
  description,
  env,
  fetchGeoLocation: getGeoLocation,
  fetchMember: getMember,
  gAppId,
  history,
  identityHost,
  isFetchingLocation,
  loginIfVerified,
  meta,
  name: pageName,
  oneTapError,
  path,
  payHost,
  payNgHost,
  primaryImageObjDetails,
  productSchema,
  schema,
  state,
  title,
  trackerHost,
  user,
  withFooter,
  withNav,
  withTopAd,
}) {
  const username = user?.name;
  const avatar = user?.imageUrl;
  const credential = useGoogleOneTap(gAppId, !!username);
  const [showMismatchModal, setShowMismatchModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const toggleMismatchModal = () => {
    setShowMismatchModal(prevStatus => !prevStatus);
  };

  const toggleSignupModal = () => {
    setShowSignupModal(prevStatus => !prevStatus);
  };

  const reloadPage = () => window.location.reload();

  useEffect(() => {
    if (typeof credential !== 'undefined' && authLoginStatus === LoginStatus.LoggedOut) {
      loginIfVerified({ identityHost, token: credential })
        .then(({ payload }) => {
          if (payload.status === 'success') {
            localyticsEvent(ANALYTICS_EVENTS.LOGGED_IN, {
              Method: 'googleonetap',
            });

            ReactGTM.event({
              event: 'LoginComplete',
              userID: payload.accountId,
              method: 'googleonetap',
            });

            reloadPage();
          }
        });
    }
  }, [credential, loginIfVerified, authLoginStatus, identityHost]);

  useEffect(() => {
    if (authLoginStatus === LoginStatus.AccountNotFound) {
      // toggle signupform that allows users to signup in consumer-web
      toggleSignupModal();
    } else if (authLoginStatus === LoginStatus.AccountNotLinked) {
      // redirect to identity with preloaded state in order to link accounts
      window.location = `//${identityHost}/social/login/google?token=${credential}`;
    }
  }, [identityHost, credential, authLoginStatus]);

  useEffect(() => {
    if (authLoginStatus === LoginStatus.EmailMismatch && typeof credential !== 'undefined') {
      toggleMismatchModal();
    }
  }, [authLoginStatus, credential]);

  useEffect(() => {
    if (username && typeof avatar === 'undefined') {
      getMember({ memberId: username });
    }
  }, [getMember, avatar, username]);

  useEffect(() => {
    if (!isFetchingLocation && !country) {
      getGeoLocation();
    }
  }, [getGeoLocation, isFetchingLocation, country]);

  useEffect(() => {
    pageView(pageName, meta);
  }, [pageName, meta]);

  const onSocialFormSignup = useCallback(({ data }) => {
    toggleSignupModal();
    if (data?.response?.accountId) {
      localyticsEvent(ANALYTICS_EVENTS.REGISTERED, {
        Method: 'googleonetap',
      });

      ReactGTM.event({
        event: 'SignupComplete',
        userID: data.response.accountId,
        method: 'googleonetap',
      });

      reloadPage();
    }
  }, []);

  const generateAccountObject = useMemo(() => {
    if (!username) {
      return undefined;
    }

    return {
      username: truncateForDisplay(username),
      avatar,
    };
  }, [avatar, username]);

  const showPayLink = country && COUNTRIES_WITH_PAY.includes(country.toUpperCase());

  const handleNavbarItemClick = useCallback((e) => {
    const { currentTarget, target } = e;
    const linkName = target.innerText;

    // menu item is in a span. Current target gives us the element that actually has the event
    const linkTarget = currentTarget.target;
    const href = currentTarget.attributes.href.value;

    if (linkName === 'Vehicle Recalls') {
      localyticsEvent(ANALYTICS_EVENTS.OPEN_RECALL_SEARCH_PAGE, {
        Page: linkName,
        source: 'navbar',
      });
    } else {
      localyticsEvent(ANALYTICS_EVENTS.MAIN_MENU_ITEM_CLICK, {
        name: linkName,
        href,
        'Page Type': pageName,
      });
    }

    const isRelative = href.charAt(0) === '/' && href.charAt(1) !== '/';
    const opensNewTab = linkTarget === '_blank';
    const shouldRedirect = ['/login', '/logout', '/signup', '/password/change'].includes(href);

    if (isRelative && !opensNewTab && !shouldRedirect) {
      e.preventDefault();
      history.push(href);
    }
  }, [history, pageName]);

  const handleFooterItemClick = useCallback((e) => {
    const { currentTarget, target } = e;
    const linkName = target.innerText;
    const href = currentTarget.attributes.href.value;

    localyticsEvent(ANALYTICS_EVENTS.FOOTER_ITEM_CLICK, {
      name: linkName,
      href,
      'Page Type': pageName,
    });
  }, [pageName]);

  const mainMenuItems = [...NavBar.defaultProps.mainLinkGroups];

  if (!showPayLink) {
    mainMenuItems.splice(2, 1);
  }

  const applyCcpa = canShowDoNotSell(state, country);

  const cleanPath = path.endsWith('/') ? path.substring(0, path.lastIndexOf('/')) : path;
  const canonicalPath = canonicalizeString(cleanPath, true);

  const pageTitle = title || TITLE;
  const pageDescription = description || DESCRIPTION;

  const redirectToIdentity = () => { window.location = `//${identityHost}`; };

  return (
    <div>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="viewport" content="width=device-width, initial-scale=1, user-scalable=0" />
        <meta content="IE=edge,chrome=1" httpEquiv="X-UA-Compatible" />
        <meta name="google-site-verification" content="zXhVMaZ-JnjK_v5xNuzXXzO99G76HlIJhiyP31rNWsQ" />
        <meta name="facebook-domain-verification" content="9i95xs7r3tr0pkido9vsncfgkqyipu" />
        <meta name="apple-itunes-app" content="app-id=406719683, app-argument=https://itunes.apple.com/us/app/gasbuddy-find-cheap-gas-prices/id406719683?mt=8&uo=4" />
        <meta name="robots" content={env === 'production' ? 'max-snippet:-1, max-image-preview:large, max-video-preview:-1' : 'noindex, nofollow'} />
        <meta name="description" content={pageDescription} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:image" content={THUMBNAIL} />
        <meta property="og:description" content={pageDescription} />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:image" content={THUMBNAIL} />
        <meta name="twitter:description" content={pageDescription} />
        <link rel="canonical" href={`https://www.gasbuddy.com${canonicalPath}`} />
        <link rel="shortcut icon" id="favicon" type="image/x-icon" href="https://static.gasbuddy.com/web/favicon.ico" />
        <link rel="apple-touch-icon" sizes="152x152" href="https://images.gasbuddy.com/images/websites/gasbuddy/apple-touch-icon.png" />
        <link rel="alternate" href="android-app://gbis.gbandroid/gasbuddy/" />
        <script async defer src="https://www.googletagservices.com/tag/js/gpt.js" type="text/javascript" />
        <script type="text/javascript">
          {`
            window.googletag = window.googletag || {cmd: []};
          `}
        </script>
        <script type="text/javascript">
          {'ggv2id = \'bfce2827\';'}
        </script>
        <script async defer src="https://js.gumgum.com/services.js" type="text/javascript" />
        <script type="text/javascript">
          {`
            !function(a9,a,p,s,t,A,g){if(a[a9])return;function q(c,r){a[a9]._Q.push([c,r])}a[a9]={init:function(){q("i",arguments)},fetchBids:function(){q("f",arguments)},setDisplayBids:function(){},targetingKeys:function(){return[]},_Q:[]};A=p.createElement(s);A.async=!0;A.src=t;g=p.getElementsByTagName(s)[0];g.parentNode.insertBefore(A,g)}("apstag",window,document,"script","//c.amazon-adsystem.com/aax2/apstag.js");

            apstag.init({
              pubID: '3615',
              adServer: 'googletag',
              bidTimeout: 30 * 1000
            });
          `}
        </script>
        <script type="text/javascript">
          {'var _vwo_code=(function(){ var account_id=344491, settings_tolerance=2000, library_tolerance=2500, use_existing_jquery=false, /* DO NOT EDIT BELOW THIS LINE */ f=false,d=document;return{use_existing_jquery:function(){return use_existing_jquery;},library_tolerance:function(){return library_tolerance;},finish:function(){if(!f){f=true;var a=d.getElementById(\'_vis_opt_path_hides\');if(a)a.parentNode.removeChild(a);}},finished:function(){return f;},load:function(a){var b=d.createElement(\'script\');b.src=a;b.type=\'text/javascript\';b.innerText;b.onerror=function(){_vwo_code.finish();};d.getElementsByTagName(\'head\')[0].appendChild(b);},init:function(){settings_timer=setTimeout(\'_vwo_code.finish()\',settings_tolerance);var a=d.createElement(\'style\'),b=\'body{opacity:0 !important;filter:alpha(opacity=0) !important;background:none !important;}\',h=d.getElementsByTagName(\'head\')[0];a.setAttribute(\'id\',\'_vis_opt_path_hides\');a.setAttribute(\'type\',\'text/css\');if(a.styleSheet)a.styleSheet.cssText=b;else a.appendChild(d.createTextNode(b));h.appendChild(a);this.load(\'//dev.visualwebsiteoptimizer.com/j.php?a=\'+account_id+\'&u=\'+encodeURIComponent(d.URL)+\'&r=\'+Math.random());return settings_timer;}};}());_vwo_settings_timer=_vwo_code.init();'}
        </script>
      </Helmet>
      <Helmet>
        <script src="https://accounts.google.com/gsi/client" async defer />
      </Helmet>
      <Modal
        content={({ close }) => (
          <div className={cx('mismatchModal')}>
            <Header as="h2">Sorry, we can&apos;t log you in at the current time.</Header>
            <Text as="p">Please log in with your email or continue browsing GasBuddy as a guest.</Text>
            <br />
            <Button onClick={redirectToIdentity} primary wide>Log in</Button>
            <Button onClick={close}>Continue as Guest</Button>
          </div>
        )}
        onClose={toggleMismatchModal}
        size="sm"
        forceIsShowing={showMismatchModal}
      />
      {
        showSignupModal && (
          <SocialSignupLazyComponent
            identityHost={identityHost}
            email={oneTapError?.email}
            credential={credential}
            onSocialFormSignup={onSocialFormSignup}
            toggleSignupModal={toggleSignupModal}
            showSignupModal={showSignupModal}
          />
        )
      }
      <MainSchema
        title={title}
        description={description}
        primaryImageObjDetails={primaryImageObjDetails}
        productSchema={productSchema}
        schema={schema}
      />
      {withTopAd && (
        <AdBanner paths={bannerAds.length ? bannerAds : BANNER_ADS} params={adParams} />
      )}
      {withNav && (
        <NavBar
          onMenuItemClick={handleNavbarItemClick}
          account={generateAccountObject}
          mainLinkGroups={mainMenuItems}
          activeHrefKey={activeNavKey}
          consumerHost="" // Make consumer-web URLs relative
          identityHost="" // Make identity-web URLs relative
          payHost={payHost} // Make payment-web URLs environment-friendly
          payNgHost={payNgHost} // Make payment-web-canary URLs environment-friendly
          trackerHost={trackerHost} // Make tracker-web URLs environment-friendly
        />
      )}
      <AlertBanner featureName="homepage_banner" />
      <ErrorBoundary>
        <div className={cx('content', { padded: !compact }, className)}>
          {children}
        </div>
      </ErrorBoundary>
      {withFooter && (
        <Footer
          consumerHost="" // Make consumer-web URLs relative
          identityHost="" // Make identity-web URLs relative
          onItemClick={handleFooterItemClick}
          payHost={payHost} // Make payment-web URLs environment-friendly
          payNgHost={payNgHost} // Make payment-web-canary URLs environment-friendly
          trackerHost={trackerHost} // Make tracker-web URLs environment-friendly
          version={version}
          applyCcpa={applyCcpa}
        />
      )}
    </div>
  );
}

Page.propTypes = {
  activeNavKey: PropTypes.string,
  adParams: PropTypes.objectOf(PropTypes.any),
  authLoginStatus: PropTypes.oneOf(Object.values(LoginStatus)),
  bannerAds: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    path: PropTypes.string,
    sizes: AdSizesPropType.isRequired,
    sizeMapping: AdSizeMappingPropType,
  })),
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  compact: PropTypes.bool,
  country: PropTypes.oneOf(Object.values(COUNTRY_CODES)),
  description: PropTypes.string,
  env: PropTypes.string,
  fetchGeoLocation: PropTypes.func.isRequired,
  fetchMember: PropTypes.func.isRequired,
  gAppId: PropTypes.string,
  history: PropTypes.shape({
    push: PropTypes.func.isRequired,
  }).isRequired,
  identityHost: PropTypes.string,
  isFetchingLocation: PropTypes.bool,
  loginIfVerified: PropTypes.func,
  meta: PropTypes.shape({}),
  name: PropTypes.string,
  oneTapError: PropTypes.shape({
    status: PropTypes.number,
    message: PropTypes.string,
    email: PropTypes.string,
  }),
  path: PropTypes.string.isRequired,
  payHost: PropTypes.string,
  payNgHost: PropTypes.string,
  primaryImageObjDetails: PropTypes.shape({
    contentUrl: PropTypes.string,
    description: PropTypes.string,
    name: PropTypes.string,
  }),
  productSchema: PropTypes.shape({
    product: PropTypes.shape({}),
    helpers: PropTypes.shape({}),
  }),
  schema: PropTypes.object, // eslint-disable-line
  state: PropTypes.string,
  title: PropTypes.string,
  trackerHost: PropTypes.string,
  user: PropTypes.shape({
    imageUrl: PropTypes.string,
    name: PropTypes.string,
  }),
  withFooter: PropTypes.bool,
  withNav: PropTypes.bool,
  withTopAd: PropTypes.bool,
};

Page.defaultProps = {
  activeNavKey: null,
  adParams: undefined,
  authLoginStatus: undefined,
  bannerAds: [],
  className: undefined,
  compact: false,
  country: undefined,
  description: DESCRIPTION,
  env: 'production',
  gAppId: undefined,
  identityHost: undefined,
  isFetchingLocation: false,
  loginIfVerified: undefined,
  meta: null,
  name: undefined,
  oneTapError: undefined,
  payHost: undefined,
  payNgHost: undefined,
  primaryImageObjDetails: undefined,
  productSchema: undefined,
  schema: undefined,
  state: undefined,
  title: TITLE,
  trackerHost: undefined,
  user: undefined,
  withFooter: true,
  withNav: true,
  withTopAd: false,
};
